import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { DeviceApiKeyService } from '../../services/device-api-key.service';
import {
    Button,
    Box,
    Chip,
    Modal,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
    Tooltip,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import { useFreeTierNudge } from '../shared/FreeTierNudge';

interface DeviceApiKey {
    id: string;
    apiKey: string;
    institutionId: string;
    deviceSn: string | null;
    description: string | null;
    isActive: boolean;
    createdAt: string;
}

const DeviceApiKeyList = () => {
    const [keys, setKeys] = useState<DeviceApiKey[]>([]);
    const [loading, setLoading] = useState(true);

    // Generate dialog
    const [generateOpen, setGenerateOpen] = useState(false);
    const [description, setDescription] = useState('');
    const [deviceSn, setDeviceSn] = useState('');
    const [generating, setGenerating] = useState(false);

    // Newly generated/rotated key highlight
    const [revealedKey, setRevealedKey] = useState<string | null>(null);

    // Rotate confirmation
    const [rotateTarget, setRotateTarget] = useState<DeviceApiKey | null>(null);
    const [rotating, setRotating] = useState(false);

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState<DeviceApiKey | null>(null);
    const [deleting, setDeleting] = useState(false);

    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const toast = useToast();
    const { t } = useTranslation(['deviceApiKey', 'common']);
    const freeTier = useFreeTierNudge();

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchKeys();
    }, [isAuthenticated, navigate]);

    const fetchKeys = async () => {
        try {
            setLoading(true);
            const data = await DeviceApiKeyService.getByInstitution();
            setKeys(data);
        } catch (err: any) {
            toast.showApiError(err, t('deviceApiKeyList.toasts.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!description.trim()) return;
        try {
            setGenerating(true);
            const created: DeviceApiKey = await DeviceApiKeyService.generate(description.trim(), deviceSn.trim());
            setKeys(prev => [created, ...prev]);
            setRevealedKey(created.apiKey);
            setGenerateOpen(false);
            setDescription('');
            setDeviceSn('');
            toast.showSuccess(t('deviceApiKeyList.toasts.generated'));
        } catch (err: any) {
            if (!freeTier.handle(err)) toast.showApiError(err, t('deviceApiKeyList.toasts.generateFailed'));
        } finally {
            setGenerating(false);
        }
    };

    const handleRotate = async () => {
        if (!rotateTarget) return;
        try {
            setRotating(true);
            const updated: DeviceApiKey = await DeviceApiKeyService.rotate(rotateTarget.id);
            setKeys(prev => prev.map(k => k.id === updated.id ? updated : k));
            setRevealedKey(updated.apiKey);
            setRotateTarget(null);
            toast.showSuccess(t('deviceApiKeyList.toasts.rotated'));
        } catch (err: any) {
            toast.showApiError(err, t('deviceApiKeyList.toasts.rotateFailed'));
        } finally {
            setRotating(false);
        }
    };

    const handleToggleActive = async (key: DeviceApiKey) => {
        try {
            const updated: DeviceApiKey = await DeviceApiKeyService.toggleActive(key.id);
            setKeys(prev => prev.map(k => k.id === updated.id ? updated : k));
            toast.showSuccess(updated.isActive ? t('deviceApiKeyList.toasts.activated') : t('deviceApiKeyList.toasts.deactivated'));
        } catch (err: any) {
            toast.showApiError(err, t('deviceApiKeyList.toasts.toggleFailed'));
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await DeviceApiKeyService.deleteKey(deleteTarget.id);
            setKeys(prev => prev.filter(k => k.id !== deleteTarget.id));
            if (revealedKey === deleteTarget.apiKey) setRevealedKey(null);
            setDeleteTarget(null);
            toast.showSuccess(t('deviceApiKeyList.toasts.deleted'));
        } catch (err: any) {
            toast.showApiError(err, t('deviceApiKeyList.toasts.deleteFailed'));
        } finally {
            setDeleting(false);
        }
    };

    const copyToClipboard = (value: string) => {
        navigator.clipboard.writeText(value).then(() => {
            toast.showSuccess(t('deviceApiKeyList.toasts.copied'));
        });
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        return format(parseISO(dateStr), 'dd-MM-yyyy');
    };

    if (loading && keys.length === 0) return <div>{t('deviceApiKeyList.loading')}</div>;

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2>{t('deviceApiKeyList.title')}</h2>
                <Button variant="contained" color="primary" onClick={() => setGenerateOpen(true)}>
                    {t('deviceApiKeyList.generateNewKey')}
                </Button>
            </div>

            {revealedKey && (
                <Box sx={{
                    mb: 3, p: 2, backgroundColor: '#e8f5e9', borderRadius: 2,
                    border: '1px solid #a5d6a7', display: 'flex', alignItems: 'center', gap: 1
                }}>
                    <Box sx={{ flex: 1 }}>
                        <strong>{t('deviceApiKeyList.newKey')}</strong>
                        <Box component="code" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                            {revealedKey}
                        </Box>
                    </Box>
                    <Tooltip title={t('deviceApiKeyList.copyToClipboard')}>
                        <IconButton onClick={() => copyToClipboard(revealedKey)} size="small" aria-label={t('deviceApiKeyList.copyToClipboard')}>
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Button size="small" onClick={() => setRevealedKey(null)}>{t('deviceApiKeyList.dismiss')}</Button>
                </Box>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>{t('deviceApiKeyList.columns.description')}</th>
                        <th style={tableHeaderStyle}>{t('deviceApiKeyList.columns.deviceSn')}</th>
                        <th style={tableHeaderStyle}>{t('deviceApiKeyList.columns.status')}</th>
                        <th style={tableHeaderStyle}>{t('deviceApiKeyList.columns.key')}</th>
                        <th style={tableHeaderStyle}>{t('deviceApiKeyList.columns.created')}</th>
                        <th style={tableHeaderStyle}>{t('deviceApiKeyList.columns.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {keys.map(key => (
                        <tr key={key.id}>
                            <td style={tableCellStyle}>{key.description || '-'}</td>
                            <td style={tableCellStyle}>{key.deviceSn || <em style={{ color: '#999' }}>{t('deviceApiKeyList.any')}</em>}</td>
                            <td style={tableCellStyle}>
                                <Chip
                                    label={key.isActive ? t('common:status.active') : t('common:status.inactive')}
                                    color={key.isActive ? 'success' : 'default'}
                                    size="small"
                                />
                            </td>
                            <td style={tableCellStyle}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <code style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{key.apiKey}</code>
                                    <Tooltip title={t('deviceApiKeyList.copy')}>
                                        <IconButton size="small" onClick={() => copyToClipboard(key.apiKey)} aria-label={t('deviceApiKeyList.copy')}>
                                            <ContentCopy sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </td>
                            <td style={tableCellStyle}>{formatDate(key.createdAt)}</td>
                            <td style={tableCellStyle}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        onClick={() => setRotateTarget(key)}
                                    >
                                        {t('deviceApiKeyList.rotate')}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color={key.isActive ? 'secondary' : 'success'}
                                        onClick={() => handleToggleActive(key)}
                                    >
                                        {key.isActive ? t('deviceApiKeyList.deactivate') : t('deviceApiKeyList.activate')}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setDeleteTarget(key)}
                                    >
                                        {t('common:actions.delete')}
                                    </Button>
                                </Box>
                            </td>
                        </tr>
                    ))}
                    {keys.length === 0 && (
                        <tr>
                            <td style={tableCellStyle} colSpan={6}>{t('deviceApiKeyList.empty')}</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Generate Dialog */}
            <Modal open={generateOpen} onClose={() => { setGenerateOpen(false); setDescription(''); setDeviceSn(''); }}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 420, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
                }}>
                    <h3 style={{ marginTop: 0 }}>{t('deviceApiKeyList.generateDialog.title')}</h3>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label={t('deviceApiKeyList.generateDialog.description')}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            fullWidth
                            required
                            placeholder={t('deviceApiKeyList.generateDialog.descriptionPlaceholder')}
                        />
                        <TextField
                            label={t('deviceApiKeyList.generateDialog.deviceSn')}
                            value={deviceSn}
                            onChange={e => setDeviceSn(e.target.value)}
                            fullWidth
                            placeholder={t('deviceApiKeyList.generateDialog.deviceSnPlaceholder')}
                            helperText={t('deviceApiKeyList.generateDialog.deviceSnHelper')}
                        />
                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={() => { setGenerateOpen(false); setDescription(''); setDeviceSn(''); }} variant="outlined">
                            {t('common:actions.cancel')}
                        </Button>
                        <Button
                            onClick={handleGenerate}
                            variant="contained"
                            color="primary"
                            disabled={!description.trim() || generating}
                        >
                            {generating ? t('deviceApiKeyList.generateDialog.generating') : t('deviceApiKeyList.generateDialog.generate')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Rotate Confirmation */}
            <Dialog open={!!rotateTarget} onClose={() => setRotateTarget(null)}>
                <DialogTitle>{t('deviceApiKeyList.rotateDialog.title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Trans
                            t={t}
                            i18nKey="deviceApiKeyList.rotateDialog.text"
                            values={{ name: rotateTarget?.description || t('deviceApiKeyList.thisEntry') }}
                            components={{ strong: <strong /> }}
                        />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRotateTarget(null)}>{t('common:actions.cancel')}</Button>
                    <Button onClick={handleRotate} color="warning" variant="contained" disabled={rotating}>
                        {rotating ? t('deviceApiKeyList.rotateDialog.rotating') : t('deviceApiKeyList.rotateDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>{t('deviceApiKeyList.deleteDialog.title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Trans
                            t={t}
                            i18nKey="deviceApiKeyList.deleteDialog.text"
                            values={{ name: deleteTarget?.description || t('deviceApiKeyList.thisEntry') }}
                            components={{ strong: <strong /> }}
                        />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>{t('common:actions.cancel')}</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
                        {deleting ? t('deviceApiKeyList.deleteDialog.deleting') : t('common:actions.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
            {freeTier.element}
        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default DeviceApiKeyList;
